<template>
  <v-container class="mt-5">
    <alert />
    <v-data-table
      :headers="headers"
      :items="directors"
      dense
      class="elevation-1"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title class="text-h6">Directors</v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-spacer></v-spacer>
          <v-dialog persistent v-model="dialog" max-width="700">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                outlined
                color="primary"
                dark
                class="mb-2"
                v-bind="attrs"
                v-on="on"
                @click="clearForm"
              >
                Add Director
              </v-btn>
            </template>
            <v-card>
              <v-card-title class="primary white--text">
                <span class="text-h5">
                  {{ edit ? "Edit" : "Add" }} Directors
                </span>
              </v-card-title>

              <v-card-text class="mt-5">
                <v-container>
                  <v-form ref="directorForm" v-model="isValid">
                    <v-row dense>
                      <v-col cols="12">
                        <v-alert
                          dense
                          text
                          type="info"
                          border="left"
                          class="mb-4"
                        >
                          <div class="d-flex align-center">
                            <v-icon color="info" class="mr-2"
                              >mdi-information-outline</v-icon
                            >
                            <span class="text-body-2">
                              <strong>Quick Guide:</strong> First select the
                              <strong>Director Type</strong>. For
                              <strong>Members</strong>, use the search field to
                              quickly find and auto-fill details. For
                              <strong>Non-Members</strong>, enter details
                              manually.
                            </span>
                          </div>
                        </v-alert>
                      </v-col>

                      <v-col cols="12">
                        <v-select
                          outlined
                          placeholder="Select Director Type"
                          label="Director Type"
                          v-model="formData.type"
                          :items="directorTypes"
                          dense
                          :rules="rules.type"
                        ></v-select>
                      </v-col>

                      <v-col cols="12" v-if="isMember">
                        <v-autocomplete
                          outlined
                          dense
                          v-model="selectedMember"
                          :items="searchResults"
                          :loading="loadingSearch"
                          :search-input.sync="search"
                          color="primary"
                          hide-no-data
                          hide-selected
                          item-text="SearchName"
                          item-value="MemberNo"
                          label="Search Member (Name / No / Profile ID)"
                          placeholder="Start typing to search..."
                          prepend-inner-icon="mdi-magnify"
                          return-object
                          clearable
                          :rules="isMember ? rules.required : []"
                          @change="onMemberSelect"
                        >
                          <template v-slot:item="{ item }">
                            <v-list-item-content>
                              <v-list-item-title>
                                {{ item.FirstName }} {{ item.SecondName }}
                                {{ item.LastName }}
                              </v-list-item-title>
                              <v-list-item-subtitle>
                                No: {{ item.MemberNo }} | ID:
                                {{ item.ProfileID }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </template>
                        </v-autocomplete>
                      </v-col>

                      <v-col cols="12" sm="6" md="6">
                        <v-text-field
                          outlined
                          placeholder="Director Name"
                          label="Director Name"
                          v-model="formData.name"
                          dense
                          :rules="rules.name"
                          :readonly="isMember"
                          :background-color="isMember ? 'grey lighten-4' : ''"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="6">
                        <v-text-field
                          outlined
                          placeholder="Identification Number"
                          label="Identification Number"
                          v-model="formData.identificationDocumentNo"
                          dense
                          :rules="rules.identificationDocumentNo"
                          persistent-hint
                          :hint="
                            isMember
                              ? 'Auto-filled, but can be updated if necessary'
                              : ''
                          "
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="12" md="12">
                        <v-autocomplete
                          outlined
                          placeholder="Select Discipline"
                          label="Discipline"
                          v-model="formData.discipline"
                          :items="disciplines"
                          item-text="discipline"
                          item-value="discipline"
                          dense
                          :rules="rules.discipline"
                        ></v-autocomplete>
                      </v-col>
                      <v-col cols="12" sm="12" md="12" v-if="isMember">
                        <v-text-field
                          outlined
                          placeholder="Professional Registration Number"
                          label="Professional Registration Number"
                          v-model="formData.regNo"
                          dense
                          readonly
                          background-color="grey lighten-4"
                          :rules="isMember ? rules.regNo : []"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-form>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="clearForm"> Cancel </v-btn>
                <v-btn color="primary" @click="save"> Save </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon small class="mr-2" color="blue" @click="update(item)">
          mdi-pencil
        </v-icon>
        <v-icon small color="red" @click="remove(item)"> mdi-delete </v-icon>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { helper } from "../../../../utils";
import Alert from "../../../../components/Alert";
import SetupMixin from "../../../setup/SetupMixin";

export default {
  name: "Director",
  components: { Alert },
  mixins: [SetupMixin],
  data: () => ({
    dialog: false,
    edit: false,
    isValid: true,
    search: null,
    selectedMember: null,
    directorTypes: ["Member", "Non Member"],
    formData: {
      name: "",
      discipline: "",
      regNo: "",
      identificationDocumentNo: "",
      type: "",
    },
    headers: [
      {
        text: "Director Name",
        align: "start",
        sortable: false,
        value: "name",
      },
      { text: "ID Number", value: "identificationDocumentNo" },
      { text: "Discipline", value: "discipline" },
      { text: "Type", value: "type" },
      { text: "Professional Registration Number", value: "regNo" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    rules: {
      required: [(v) => !!v || "This field is required"],
      name: [(v) => !!v || "name is required"],
      discipline: [(v) => !!v || "Discipline is required"],
      regNo: [(v) => !!v || "Professional Registration Number is required"],
      type: [(v) => !!v || "type is required"],
      identificationDocumentNo: [
        (v) => !!v || "identificationDocumentNo is required",
      ],
    },
    searchTimer: null,
  }),

  mounted() {
    this.$store.dispatch("Profile/directorStore/getDirectors", {
      ProfileID: this.$route.params?.firmCode,
    });
    this.getSetups(["getDisciplines"]);
  },

  watch: {
    search(val) {
      if (!val || val.length < 3) return;
      if (this.loadingSearch) return;

      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.$store.dispatch("Profile/directorStore/searchMembers", val);
      }, 500);
    },
    "formData.type"(val) {
      if (val === "Non Member") {
        this.selectedMember = null;
        this.formData.regNo = "";
        this.formData.name = "";
        this.formData.identificationDocumentNo = "";
        this.formData.discipline = "";
      }
    },
  },

  computed: {
    isMember() {
      return this.formData.type === "Member";
    },

    directors() {
      return this.$store.getters["Profile/directorStore/directors"];
    },

    searchResults() {
      return this.$store.getters["Profile/directorStore/searchResults"];
    },

    loadingSearch() {
      return this.$store.getters["Profile/directorStore/loadingSearch"];
    },
  },

  methods: {
    onMemberSelect(member) {
      if (member) {
        this.formData.regNo = member.MemberNo;
        this.formData.name = `${member.FirstName} ${
          member.SecondName ? member.SecondName + " " : ""
        }${member.LastName}`;
        this.formData.identificationDocumentNo = member.IDNo || "";
        this.formData.discipline = member.Discipline || "";
      } else {
        this.formData.regNo = "";
        this.formData.name = "";
        this.formData.identificationDocumentNo = "";
        this.formData.discipline = "";
      }
    },

    clearForm: function () {
      this.formData = {
        name: "",
        discipline: "",
        regNo: "",
        identificationDocumentNo: "",
        type: "",
      };
      this.selectedMember = null;
      this.search = null;
      this.$store.dispatch("Profile/directorStore/searchMembers", null);
      this.dialog = false;
      this.edit = false;
      if (this.$refs.directorForm) {
        this.$refs.directorForm.resetValidation();
      }
    },

    update: function (payload) {
      this.formData = { ...payload };
      this.edit = true;
      this.dialog = true;
    },

    remove(item) {
      this.$confirm.show({
        title: "Delete this Director?",
        text: `Are you sure you want to delete?`,
        onConfirm: () => {
          this.$store.dispatch("Profile/directorStore/removeDirector", {
            ...item,
          });
        },
      });
    },

    save: function () {
      if (this.$refs.directorForm.validate()) {
        let data = { ...this.formData };
        data.profileID = this.$route.params.firmCode;

        if (this.edit) {
          data.entryNo = this.formData.entryNo
            ? parseInt(this.formData.entryNo)
            : parseInt(this.directors.length + 1);
        }

        this.$store.dispatch(
          `Profile/directorStore/${
            this.edit ? "updateDirector" : "addDirector"
          }`,
          helper.prepareFormData(data)
        );

        this.clearForm();
      }
    },
  },
};
</script>

<style scoped></style>
